/**
 * Lookbook Section
 * Handles activity filtering, hotspot interactions, and mobile drawer.
 */
class Lookbook {
  constructor(sectionEl) {
    this.section = sectionEl;
    this.sectionId = sectionEl.dataset.sectionId;
    this.grid = sectionEl.querySelector('.lookbook__grid');
    this.overlay = sectionEl.querySelector('.lookbook__overlay');
    this.drawer = sectionEl.querySelector('.lookbook__drawer');
    this.drawerBody = sectionEl.querySelector('.lookbook__drawer-body');
    this.drawerClose = sectionEl.querySelector('.lookbook__drawer-close');
    this.drawerTitle = sectionEl.querySelector('.lookbook__drawer-title');
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.activeCard = null;
    this.activeFilter = 'all';
    this.activeFilters = new Set(['all']);

    this.#initLayout();
    this.#initFilters();
    this.#initHotspots();
    this.#initDrawer();
    this.#initPixelFetcher();
  }

  showCardForEditor(hotspot, card, item) {
    if (this.activeCard && this.activeCard !== card) {
      this.#hideCard(this.activeCard);
    }

    this.#positionCard(hotspot, card, item);
    card.classList.add('lookbook__product-card--visible');
    card.dataset.visible = 'true';
    this.activeCard = card;
  }

  /* ---------- Layout Engine (Hybrid Grid/Masonry) ---------- */

  #initLayout() {
    this.#shuffleDOM();

    window.addEventListener('resize', () => {
      requestAnimationFrame(() => this.#applyLayoutMode());
    });

    // Ensure images load properly if they are pre-cached before layout calculation
    setTimeout(() => this.#applyLayoutMode(), 50);
  }

  #isShuffleActive() {
    const shuffleDesktop = this.grid.hasAttribute('data-shuffle-desktop');
    const shuffleMobile = this.grid.hasAttribute('data-shuffle-mobile');
    return (this.desktopQuery.matches && shuffleDesktop) || (!this.desktopQuery.matches && shuffleMobile);
  }

  #applyLayoutMode() {
    // If filters are active (not 'all'), force Masonry waterfall mode to collapse whitespace.
    // Otherwise, depend on Shuffle preference (Masonry if shuffle active, original Grid if not).
    const forceMasonry = !this.activeFilters.has('all');
    const useMasonry = forceMasonry || this.#isShuffleActive();

    if (useMasonry) {
      this.grid.classList.add('lookbook__grid--masonry');
      this.#layoutMasonry();
    } else {
      this.grid.classList.remove('lookbook__grid--masonry');
      this.#resetGridInlineStyles();
    }
  }

  #resetGridInlineStyles() {
    const items = this.grid.querySelectorAll('.lookbook__item');
    items.forEach(item => {
      item.style.width = '';
      item.style.transform = '';
    });
    this.grid.style.height = '';
  }

  #shuffleDOM() {
    const seedAttr = this.grid.dataset.shuffleSeed || this.grid.dataset.shuffleWeek;
    if (!seedAttr) return;

    const shuffleDesktop = this.grid.hasAttribute('data-shuffle-desktop');
    const shuffleMobile = this.grid.hasAttribute('data-shuffle-mobile');

    if (!shuffleDesktop && !shuffleMobile) return;

    const seed = parseInt(seedAttr, 10) || 0;
    const items = Array.from(this.grid.querySelectorAll('.lookbook__item'));

    if (items.length < 2) {
      this.grid.classList.remove('lookbook__grid--shuffle-pending');
      return;
    }

    if (this.desktopQuery.matches && shuffleDesktop) {
      const orderMap = seededShuffle(items.map((_, i) => i), seed);
      for (let i = 0; i < items.length; i++) {
        this.grid.appendChild(items[orderMap[i]]);
      }
    } else if (!this.desktopQuery.matches && shuffleMobile) {
      const orderMap = seededShuffle(items.map((_, i) => i), seed + 1);
      for (let i = 0; i < items.length; i++) {
        this.grid.appendChild(items[orderMap[i]]);
      }
    }

    this.grid.classList.remove('lookbook__grid--shuffle-pending');
    this.grid.classList.add('lookbook__grid--shuffle-ready');
  }

  #layoutMasonry() {
    const isMobile = !this.desktopQuery.matches;
    const columns = isMobile ? 2 : parseInt(this.grid.dataset.columns || 3, 10);
    const gap = parseInt(this.grid.dataset.gap || 4, 10);
    const containerWidth = this.grid.clientWidth;

    if (containerWidth === 0) {
      setTimeout(() => this.#layoutMasonry(), 100);
      return;
    }

    const colWidth = Math.floor((containerWidth - (gap * (columns - 1))) / columns);
    const colHeights = Array(columns).fill(0);

    const activeItems = Array.from(this.grid.querySelectorAll('.lookbook__item')).filter(
      (item) => !item.classList.contains('lookbook__item--hidden')
    );

    activeItems.forEach((item) => {
      const mobileSpan = parseInt(item.dataset.mobileColSpan || 1, 10);
      const desktopSpan = parseInt(item.dataset.colSpan || 1, 10);
      let span = isMobile ? mobileSpan : desktopSpan;
      span = Math.min(span, columns);

      const aspectRatio = parseFloat(item.dataset.aspectRatio) || 1;

      if (span === 1) {
        let minHeight = Math.min(...colHeights);
        let colIndex = colHeights.indexOf(minHeight);

        let itemHeight = Math.floor(colWidth / aspectRatio);

        item.style.width = `${colWidth}px`;
        item.style.transform = `translate(${colIndex * (colWidth + gap)}px, ${minHeight}px)`;

        colHeights[colIndex] = minHeight + itemHeight + gap;
      } else {
        let minSeqHeight = Infinity;
        let minSeqStart = 0;

        for (let i = 0; i <= columns - span; i++) {
          let seqMax = Math.max(...colHeights.slice(i, i + span));
          if (seqMax < minSeqHeight) {
            minSeqHeight = seqMax;
            minSeqStart = i;
          }
        }

        let itemWidth = colWidth * span + gap * (span - 1);
        let itemHeight = Math.floor(itemWidth / aspectRatio);

        item.style.width = `${itemWidth}px`;
        item.style.transform = `translate(${minSeqStart * (colWidth + gap)}px, ${minSeqHeight}px)`;

        for (let i = minSeqStart; i < minSeqStart + span; i++) {
          colHeights[i] = minSeqHeight + itemHeight + gap;
        }
      }
    });

    this.grid.style.height = `${Math.max(...colHeights, 0)}px`;
  }

  /* ---------- The Pixel Fetcher (Lazy Rendering) ---------- */

  #initPixelFetcher() {
    const items = this.grid.querySelectorAll('.lookbook__item');
    if (items.length === 0) return;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const item = entry.target;
          item.classList.remove('lb-lazy-hidden');
          obs.unobserve(item);
        }
      });
    }, {
      rootMargin: '100px 0px', // Fetch 100px before user scrolls
      threshold: 0.1
    });

    items.forEach(item => observer.observe(item));
  }

  /* ---------- Filtering ---------- */

  #initFilters() {
    const filterBtns = this.section.querySelectorAll('.lookbook__filter-btn');

    if (filterBtns.length === 0) return;
    
    // We'll use an array or a Set for active filters instead of a string
    this.activeFilters = new Set();
    this.activeFilters.add('all');

    for (const btn of filterBtns) {
      btn.addEventListener('click', () => {
        const filter = btn.dataset.filter;

        if (filter === 'all') {
          // If "all" is clicked, clear all other filters and select just "all"
          this.activeFilters.clear();
          this.activeFilters.add('all');
        } else {
          // If a specific filter is clicked
          
          // 1. Remove "all" if it was active
          if (this.activeFilters.has('all')) {
            this.activeFilters.delete('all');
          }
          
          // 2. Toggle this filter's active state
          if (this.activeFilters.has(filter)) {
            this.activeFilters.delete(filter);
            
            // If we just removed the last filter, revert to "all"
            if (this.activeFilters.size === 0) {
              this.activeFilters.add('all');
            }
          } else {
            this.activeFilters.add(filter);
          }
        }

        // Apply visual active states to all buttons
        for (const b of filterBtns) {
          const btnFilter = b.dataset.filter;
          if (this.activeFilters.has(btnFilter)) {
            b.classList.add('lookbook__filter-btn--active');
            b.setAttribute('aria-selected', 'true');
          } else {
            b.classList.remove('lookbook__filter-btn--active');
            b.setAttribute('aria-selected', 'false');
          }
        }

        // Execute the visual filtering
        this.#applyFilters();
      });
    }
  }

  #applyFilters() {
    const items = this.grid.querySelectorAll('.lookbook__item');

    for (const item of items) {
      const activities = item.dataset.activities || '';

      if (this.activeFilters.has('all')) {
        item.classList.remove('lookbook__item--hidden');
      } else if (activities === '') {
        /* Items without activities (no product) — hide when filtering */
        item.classList.add('lookbook__item--hidden');
      } else {
        const activityList = activities.split(',');
        
        // Check if the item has ANY of the currently active filters
        let hasMatch = false;
        for (const activeFilter of this.activeFilters) {
          if (activityList.includes(activeFilter)) {
            hasMatch = true;
            break;
          }
        }

        if (hasMatch) {
          item.classList.remove('lookbook__item--hidden');
        } else {
          item.classList.add('lookbook__item--hidden');
        }
      }
    }

    this.#applyLayoutMode();
  }

  /* ---------- Hotspot interactions ---------- */

  #initHotspots() {
    const items = this.section.querySelectorAll('.lookbook__item--has-product');

    for (const item of items) {
      const hotspots = item.querySelectorAll('.lookbook__hotspot');

      for (const hotspot of hotspots) {
        const idx = hotspot.dataset.hotspotIndex;
        const card = item.querySelector(`.lookbook__product-card[data-hotspot-index="${idx}"]`);

        if (!card) continue;

        let hideTimer = null;

        const cancelHide = () => {
          if (hideTimer) {
            clearTimeout(hideTimer);
            hideTimer = null;
          }
        };

        const scheduleHide = () => {
          cancelHide();
          hideTimer = setTimeout(() => {
            this.#hideCard(card);
          }, 300);
        };

        card.addEventListener('mouseenter', () => {
          if (this.desktopQuery.matches) {
            cancelHide();
          }
        });

        card.addEventListener('mouseleave', () => {
          if (this.desktopQuery.matches) {
            scheduleHide();
          }
        });

        this.#bindHotspot(hotspot, card, item, cancelHide, scheduleHide);
      }
    }
  }

  #bindHotspot(hotspot, card, item, cancelHide, scheduleHide) {
    hotspot.addEventListener('mouseenter', () => {
      if (this.desktopQuery.matches) {
        cancelHide();
        this.#showCard(hotspot, card, item);
      }
    });

    hotspot.addEventListener('mouseleave', () => {
      if (this.desktopQuery.matches) {
        scheduleHide();
      }
    });

    hotspot.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.desktopQuery.matches) {
        this.#toggleCard(hotspot, card, item);
      } else {
        this.#openDrawer(hotspot, card);
      }
    });

    hotspot.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();

        if (this.desktopQuery.matches) {
          this.#toggleCard(hotspot, card, item);
        } else {
          this.#openDrawer(hotspot, card);
        }
      }
    });
  }

  #showCard(hotspot, card, item) {
    if (this.activeCard && this.activeCard !== card) {
      this.#hideCard(this.activeCard);
    }

    this.#positionCard(hotspot, card, item);

    card.classList.add('lookbook__product-card--visible');
    card.dataset.visible = 'true';
    item.classList.add('lookbook__item--active');
    card._parentItem = item;
    
    this.activeCard = card;
  }

  #hideCard(card) {
    card.classList.remove('lookbook__product-card--visible');
    delete card.dataset.visible;

    if (card._parentItem) {
      card._parentItem.classList.remove('lookbook__item--active');
    }

    if (this.activeCard === card) {
      this.activeCard = null;
    }
  }

  #toggleCard(hotspot, card, item) {
    if (card.dataset.visible === 'true') {
      this.#hideCard(card);
    } else {
      this.#showCard(hotspot, card, item);
    }
  }

  #positionCard(hotspot, card, item) {
    // Get the boundaries of the whole lookbook grid to prevent clipping, 
    // rather than just the individual item box.
    const sectionRect = this.grid.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const hotspotRect = hotspot.getBoundingClientRect();
    let cardWidth = 280;
    // Increased gap so the card appears further from the cursor/hotspot
    const gap = 24; 

    /* Temporarily show card to measure height */
    const prev = {
      display: card.style.display,
      visibility: card.style.visibility,
      opacity: card.style.opacity,
    };
    card.style.display = card.classList.contains('lookbook__product-card--dual') ? 'flex' : 'block';
    card.style.visibility = 'hidden';
    card.style.opacity = '0';
    const cardHeight = card.offsetHeight || 350;
    cardWidth = card.offsetWidth || 280;
    card.style.display = prev.display;
    card.style.visibility = prev.visibility;
    card.style.opacity = prev.opacity;

    // Calculate hotspot center coordinates RELATIVE to the item container
    // (since the card is absolute positioned inside the item)
    const hotspotCenterX = hotspotRect.left - itemRect.left + hotspotRect.width / 2;
    const hotspotCenterY = hotspotRect.top - itemRect.top + hotspotRect.height / 2;

    // Calculate hotspot center coordinates RELATIVE to the viewport
    const viewportCenterX = hotspotRect.left + hotspotRect.width / 2;
    const viewportCenterY = hotspotRect.top + hotspotRect.height / 2;

    /* Try placing to the right of the hotspot first */
    let left = hotspotCenterX + gap + hotspotRect.width / 2;
    let top = hotspotCenterY - cardHeight / 2;

    /* Check horizontal boundaries against the full section (viewport width) */
    const projectedRightEdge = viewportCenterX + gap + hotspotRect.width / 2 + cardWidth;
    
    // If it overflows the right edge of the *section*, flip to the left
    if (projectedRightEdge > sectionRect.right) {
      left = hotspotCenterX - gap - hotspotRect.width / 2 - cardWidth;
      
      // If it still overflows on the left side after flipping (e.g. extremely narrow screen),
      // force it to the edge of the section on the left
      const projectedLeftEdge = viewportCenterX - gap - hotspotRect.width / 2 - cardWidth;
      if (projectedLeftEdge < sectionRect.left) {
         left = sectionRect.left - itemRect.left + 12; // 12px from left edge
      }
    }

    /* Check vertical boundaries against the full section */
    const projectedTopEdge = viewportCenterY - cardHeight / 2;
    const projectedBottomEdge = viewportCenterY + cardHeight / 2;

    // Shift down if clipping top of section
    if (projectedTopEdge < sectionRect.top) {
      // Adjust top downwards by the overflow amount
      top += (sectionRect.top - projectedTopEdge) + 12; // 12px padding
    } 
    // Shift up if clipping bottom of section
    else if (projectedBottomEdge > sectionRect.bottom) {
      // Adjust top upwards by the overflow amount
      top -= (projectedBottomEdge - sectionRect.bottom) + 12; // 12px padding
    }

    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
  }


  /* ---------- Mobile drawer ---------- */

  #initDrawer() {
    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', () => {
        this.#closeDrawer();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.#closeDrawer();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#closeDrawer();
      }
    });

    // Automatically close lookbook drawer when cart drawer or ayaq-cart-modal opens
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (cartDrawer.classList.contains('active')) {
              this.#closeDrawer();
            }
          }
        });
      });
      observer.observe(cartDrawer, { attributes: true });
    }

    const ayaqCartModal = document.querySelector('ayaq-cart-modal');
    if (ayaqCartModal) {
      const modalObserver = new MutationObserver(() => {
        const container = ayaqCartModal.querySelector('.ayaq-cart-modal-container');
        if (container && container.classList.contains('is-open')) {
          this.#closeDrawer();
        }
      });
      modalObserver.observe(ayaqCartModal, { attributes: true, childList: true, subtree: true });
    }

    // Swipe to dismiss logic
    if (this.drawer) {
      let startY = 0;
      let currentY = 0;
      let isDragging = false;
      let isAtTopWhenDragStarted = false;

      this.drawer.addEventListener('touchstart', (e) => {
        startY = e.touches[0].clientY;
        isAtTopWhenDragStarted = this.drawer.scrollTop <= 0;
        isDragging = true;
        this.drawer.style.transition = 'none'; // Remove transition for 1:1 drag
      }, { passive: true });

      this.drawer.addEventListener('touchmove', (e) => {
        if (!isDragging || !isAtTopWhenDragStarted) return;
        currentY = e.touches[0].clientY;
        const diffY = currentY - startY;

        // Only allow dragging downwards
        if (diffY > 0) {
          if (e.cancelable) e.preventDefault();
          this.drawer.style.transform = `translateY(${diffY}px)`;
        }
      }, { passive: false });

      this.drawer.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        this.drawer.style.transition = ''; // Restore CSS transition

        const diffY = currentY - startY;
        if (diffY > 100) { // Threshold to dismiss
          this.#closeDrawer();
        } else {
          this.drawer.style.transform = ''; // Snap back
        }
        currentY = 0;
        startY = 0;
      });
    }
  }

  #openDrawer(hotspot, card) {
    if (!this.drawer || !this.drawerBody || !this.overlay) return;

    // Track where to return the content when closing the drawer
    this.activeDrawerSourceNode = card;
    
    // Move all elements from the card to the drawer while preserving DOM objects
    while (card.firstChild) {
      this.drawerBody.appendChild(card.firstChild);
    }

    const titleHomme = hotspot.getAttribute('data-product-title') || '';
    const urlHomme = hotspot.getAttribute('data-product-url') || '#';
    const titleFemme = hotspot.getAttribute('data-product-femme-title') || titleHomme;
    const urlFemme = hotspot.getAttribute('data-product-femme-url') || urlHomme;

    const updateDrawerInfo = (title, url) => {
      if (this.drawerTitle) this.drawerTitle.textContent = title;
      const drawerLink = this.drawer.querySelector('.lookbook__drawer-link');
      if (drawerLink) drawerLink.href = url;
    };

    const isDual = card.classList.contains('lookbook__product-card--dual');
    const switchContainer = this.drawer.querySelector('.lookbook__drawer-switch');
    const switchBtns = this.drawer.querySelectorAll('.lookbook__switch-btn');
    const cardHalves = this.drawerBody.querySelectorAll('.lookbook__product-card-half');

    // Reset switch active states
    switchBtns.forEach((btn, idx) => {
      if (idx === 0) btn.classList.add('lookbook__switch-btn--active');
      else btn.classList.remove('lookbook__switch-btn--active');
    });

    if (isDual && switchContainer && cardHalves.length >= 2) {
      switchContainer.style.display = 'flex';
      cardHalves[0].classList.add('is-active');
      cardHalves[1].classList.remove('is-active');
      updateDrawerInfo(titleHomme, urlHomme);

      // Event listener using delegation to avoid cloneNode issues
      switchContainer.onclick = (e) => {
        const btn = e.target.closest('.lookbook__switch-btn');
        if (!btn || btn.classList.contains('lookbook__switch-btn--active')) return;

        switchBtns.forEach(b => b.classList.remove('lookbook__switch-btn--active'));
        btn.classList.add('lookbook__switch-btn--active');

        const index = parseInt(btn.dataset.index, 10);
        cardHalves.forEach((half, i) => {
          if (i === index) half.classList.add('is-active');
          else half.classList.remove('is-active');
        });

        if (index === 0) updateDrawerInfo(titleHomme, urlHomme);
        else updateDrawerInfo(titleFemme, urlFemme);
      };
    } else {
      if (switchContainer) switchContainer.style.display = 'none';
      updateDrawerInfo(titleHomme, urlHomme);
      if (cardHalves.length > 0) {
        cardHalves[0].classList.add('is-active');
      }
    }

    this.overlay.classList.add('lookbook__overlay--visible');

    requestAnimationFrame(() => {
      this.drawer.classList.add('lookbook__drawer--visible');
    });

    document.documentElement.classList.add('lb-scroll-locked');
    if (this.drawerClose) this.drawerClose.focus();
  }

  #closeDrawer() {
    if (!this.drawer || !this.overlay) return;

    this.drawer.classList.remove('lookbook__drawer--visible');
    this.overlay.classList.remove('lookbook__overlay--visible');
    this.drawer.style.transform = ''; // Reset any swipe transform
    document.documentElement.classList.remove('lb-scroll-locked');
    
    // Move nodes back to the original card after animation completes
    if (this.activeDrawerSourceNode && this.drawerBody) {
      setTimeout(() => {
        while (this.drawerBody.firstChild) {
          // Remove .is-active class so it doesn't leak to desktop
          if (this.drawerBody.firstChild.classList) {
             this.drawerBody.firstChild.classList.remove('is-active');
          }
          this.activeDrawerSourceNode.appendChild(this.drawerBody.firstChild);
        }
        this.activeDrawerSourceNode = null;
      }, 400); // 0.4s to match CSS transition
    }
  }
}

const seededShuffle = (arr, seed) => {
  let s = seed;
  const random = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
};

const lookbookInstances = new Map();

const getLookbookInstance = (sectionEl) => lookbookInstances.get(sectionEl);

/* Initialize all Lookbook sections on the page */
const initLookbook = () => {
  const sections = document.querySelectorAll('[data-section-type="lookbook"]');

  for (const section of sections) {
    lookbookInstances.set(section, new Lookbook(section));
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLookbook);
} else {
  initLookbook();
}

/* Re-initialize on Shopify section events (theme editor) */
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('[data-section-type="lookbook"]');

  if (section) {
    lookbookInstances.set(section, new Lookbook(section));
  }
});

/* Theme editor: highlight selected block and show first product card */
document.addEventListener('shopify:block:select', (e) => {
  const block = e.target;
  const section = block.closest('[data-section-type="lookbook"]');

  if (!section) return;

  for (const item of section.querySelectorAll('.lookbook__item--block-selected')) {
    item.classList.remove('lookbook__item--block-selected');
  }

  block.classList.add('lookbook__item--block-selected');

  const hotspot = block.querySelector('.lookbook__hotspot');

  if (hotspot) {
    const idx = hotspot.dataset.hotspotIndex;
    const card = block.querySelector(`.lookbook__product-card[data-hotspot-index="${idx}"]`);

    if (card) {
      const lookbookInstance = getLookbookInstance(section);

      if (lookbookInstance) {
        lookbookInstance.showCardForEditor(hotspot, card, block);
      }
    }
  }
});

document.addEventListener('shopify:block:deselect', (e) => {
  const block = e.target;
  const section = block.closest('[data-section-type="lookbook"]');

  if (!section) return;

  block.classList.remove('lookbook__item--block-selected');

  for (const card of block.querySelectorAll('.lookbook__product-card')) {
    card.classList.remove('lookbook__product-card--visible');
    delete card.dataset.visible;
  }
});
